import React from "react";
import { Link } from "react-router-dom";
import { Auth } from "aws-amplify";
import {
  IoMdBulb,
  IoIosEye,
  IoIosEyeOff,
  IoIosAlert,
  IoIosArrowRoundForward,
  IoIosArrowBack,
  IoIosCheckmarkCircle,
} from "react-icons/io";
import { mapAuthError } from "./authErrors";
import "./auth.css";

// Step 1: email → Auth.forgotPassword (sends code).
// Step 2: code + new password → Auth.forgotPasswordSubmit → /login.

const STEP_EMAIL = "email";
const STEP_RESET = "reset";

export default function Forgot(props) {
  const [step, setStep] = React.useState(STEP_EMAIL);
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("Masukkan email terlebih dahulu.");
      return;
    }
    setLoading(true);
    try {
      await Auth.forgotPassword(cleanEmail);
      setStep(STEP_RESET);
      setNotice(`Kode reset dikirim ke ${cleanEmail}.`);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    const cleanCode = code.trim();
    if (!cleanCode || !password) {
      setError("Kode dan password baru wajib diisi.");
      return;
    }
    if (password.length < 8) {
      setError("Password baru minimal 8 karakter.");
      return;
    }
    setLoading(true);
    try {
      await Auth.forgotPasswordSubmit(cleanEmail, cleanCode, password);
      props.history.push("/login?reset=1");
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth">
      <Link to="/landing" className="auth__brand">
        <IoMdBulb size="1.4em" color="var(--brand-700)" />
        cashflow
      </Link>
      <div className="auth__card">
        {step === STEP_EMAIL ? (
          <>
            <h1 className="auth__title">Lupa password</h1>
            <p className="auth__subtitle">
              Masukkan email Anda. Kami akan kirim kode reset.
            </p>

            {error && (
              <div className="auth__alert" role="alert">
                <IoIosAlert size="1.1em" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={sendCode} noValidate>
              <div className="auth__field">
                <label className="auth__field-label" htmlFor="fg-email">
                  Email
                </label>
                <input
                  id="fg-email"
                  type="email"
                  className="auth__input"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  disabled={loading}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="kamu@email.com"
                />
              </div>

              <button type="submit" className="auth__submit" disabled={loading}>
                {loading ? (
                  <span className="auth__spinner" />
                ) : (
                  <>
                    Kirim kode
                    <IoIosArrowRoundForward size="1.3em" />
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="auth__title">Reset password</h1>
            <p className="auth__subtitle">
              Masukkan kode dari email dan password baru Anda.
            </p>

            {notice && (
              <div className="auth__notice">
                <IoIosCheckmarkCircle /> {notice}
              </div>
            )}
            {error && (
              <div className="auth__alert" role="alert">
                <IoIosAlert size="1.1em" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={submitReset} noValidate>
              <div className="auth__field">
                <label className="auth__field-label" htmlFor="fg-code">
                  Kode reset
                </label>
                <input
                  id="fg-code"
                  type="text"
                  className="auth__input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  disabled={loading}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6 digit kode"
                  maxLength={10}
                />
              </div>

              <div className="auth__field">
                <label className="auth__field-label" htmlFor="fg-password">
                  Password baru
                </label>
                <div className="auth__password-wrap">
                  <input
                    id="fg-password"
                    type={showPw ? "text" : "password"}
                    className="auth__input"
                    autoComplete="new-password"
                    value={password}
                    disabled={loading}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimal 8 karakter"
                  />
                  <button
                    type="button"
                    className="auth__password-toggle"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={
                      showPw ? "Sembunyikan password" : "Lihat password"
                    }
                    tabIndex={-1}
                  >
                    {showPw ? <IoIosEyeOff /> : <IoIosEye />}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth__submit" disabled={loading}>
                {loading ? (
                  <span className="auth__spinner" />
                ) : (
                  <>
                    Reset password
                    <IoIosArrowRoundForward size="1.3em" />
                  </>
                )}
              </button>
            </form>
          </>
        )}

        <div className="auth__footer">
          Ingat password Anda?
          <Link to="/login">Masuk</Link>
        </div>
      </div>

      <Link to="/landing" className="auth__back">
        <IoIosArrowBack /> Kembali ke beranda
      </Link>
    </div>
  );
}
